<script setup lang="ts">
import { resolveComponent } from 'vue';
import { cn } from '@pkg/ui/lib/utils';
import type { PortableTextBlock, Image } from 'sanity';
import PortableEditor from '@pkg/portable-text-editor/web/PortableEditor.vue';
import UiButton from '@pkg/ui/components/ui/button/ButtonUi.vue';
import { baseTileVariants } from '.';
import BaseTag from '../BaseTag.vue';

export interface TileProps {
  variant?: NonNullable<Parameters<typeof baseTileVariants>[0]>['variant'];
  size?: NonNullable<Parameters<typeof baseTileVariants>[0]>['size'];
  limitLines?: boolean;
  asLink?: boolean;
  title: string;
  preTitle?: string;
  date?: string;
  description?: string | Array<PortableTextBlock>;
  tag?: string;
  actionText?: string;
  imageInContent?: boolean;
  image?: Image;
  actionStyle?: 'default' | 'text';
}

withDefaults(defineProps<TileProps>(), {
  variant: 'default',
  size: 'medium',
  limitLines: false,
  asLink: true,
  preTitle: undefined,
  date: undefined,
  description: undefined,
  tag: undefined,
  actionText: undefined,
  imageInContent: false,
  image: undefined,
  actionStyle: 'default',
});

/** Host app registers BaseImage from @pkg/ui. */
const BaseImage = resolveComponent('BaseImage');
</script>

<template>
  <div :class="cn(baseTileVariants({ variant, size, limitLines, asLink }), 'o-forwards-hover-to-button', 'flex-1')">
    <div v-if="!imageInContent && $slots.image != null" class="c-tile__image">
      <slot name="image">
        <component :is="BaseImage" v-if="image" :image="image" :height="201" />
      </slot>
    </div>
    <div class="c-tile__content">
      <div v-if="imageInContent && $slots.image != null">
        <slot name="image" />
      </div>
      <div v-if="!!$slots.icon" class="c-tile__icon">
        <slot name="icon" />
      </div>
      <BaseTag v-if="!!tag" class="c-tile__tag">
        {{ tag }}
      </BaseTag>
      <div class="c-tile__text flex flex-col flex-1">
        <h4>
          <span v-if="preTitle" class="c-tile__pre-title text-h6">
            {{ preTitle }}
          </span>
          <span class="c-tile__title block">
            {{ title }}
          </span>
        </h4>
        <p v-if="date" class="c-tile__date">
          {{ date }}
        </p>
        <div v-if="!!description || !!$slots.description" class="c-tile__description">
          <slot name="description">
            <PortableEditor :content="description" />
          </slot>
        </div>

        <div v-if="actionText" class="c-tile__action">
          <UiButton :variant="actionStyle === 'default' ? 'default' : 'text'">
            {{ actionText }}
          </UiButton>
        </div>
      </div>
      <slot />
    </div>
  </div>
</template>

<style scoped>
@import '../../../../../ui/assets/css/mixins/_mixins.index.pcss';

.c-tile {
  --text-gap: 0.5rem;
  height: 100%;

  display: flex;
  flex-direction: column;
  border: theme('borderWidth.DEFAULT') solid var(--tile-border-color);

  transition: theme('transitionDuration.tortoise') theme('transitionTiming.ease-in-out');
  transition-property: border-color, color;

  &__icon {
    color: var(--tile-icon-color);
    font-size: theme("fontSize.56");
  }

  &__image {
    &:deep(img) {
      width: 100%;
      height: 201px;
      object-position: left;
      padding: 24px 24px 0 24px;

      @screen tablet-wide {
        height: 170px;
      }
    }
  }

  &__content {
    position: relative;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    text-align: start;
    flex: 1;
    text-align: center;

    @screen tablet-wide {
      align-items: flex-start;
      padding: 1.5rem;
      text-align: left;
    }
  }

  &__text {
    gap: var(--text-gap);
  }

  &__title {
    @mixin h4;
    color: var(--theme-heading-color);
    text-transform: uppercase;

    transition: theme('transitionDuration.tortoise') theme('transitionTiming.ease-in-out');
    transition-property: border-color, color;
  }

  &__date {
    color: var(--tile-date-color);
  }

  &__pre-title {
    color: var(--theme-highlight-color);
    text-transform: uppercase;

    transition: theme('transitionDuration.tortoise') theme('transitionTiming.ease-in-out');
    transition-property: border-color, color;
  }

  &__description:has(+ .c-tile__action) {
      padding-bottom: 3rem;
  }

  &__action {
    position: absolute;
    bottom: 1rem;
  }

  &__description {
    font-family: theme('fontFamily.body');
  }

  &--center {
    align-items: center;
    text-align: center;

    & .c-tile__content {
      align-items: center;
      text-align: center;
    }
  }

  &--header {
    & .c-tile__title {
      @mixin h5;
    }

    & .c-tile__content {
      padding: 1rem;
      font-size: theme('fontSize.14');
    }
  }

  &--image-left {
    flex-direction: row;
    align-items: center;
    text-align: left;

    & .c-tile__text {
      gap: 0;

      @screen tablet-wide {
        gap: var(--text-gap);
      }
    }

    & .c-tile__image {
      &:deep(img) {
        height: 72px;
        width: 72px;
        @screen tablet-wide {
          width: 126px;
          height: 126px;
        }
      }
    }
  }

  &--large {
    & .c-tile__title {
      @mixin h3;
    }

    & .c-tile__content {
      gap: 1.5rem;
    }
  }

  &--stretch {
    height: 100%;
  }

  &--media {
    display: flex;
    justify-content: center;
    text-align: center;
    width: 100%;
    & .c-tile__date {
      display: none;
    }

    & .c-tile__content {
      text-align: center;
      align-items: center;
      justify-content: center;
      padding: 16px;
    }

    & .c-tile__image {
      max-width: 160px;
      align-self: center;
      padding-top: 24px;
    }

    & .c-tile__action {
      position: relative;
      text-align: center;
      padding-top: 32px;
    }
  }

  &--as-link {
    &:hover {
      border-color: var(--tile-border-color-hover);
    }

    &:hover {
      & .c-tile__title {
        color: var(--tile-border-color-hover);
      }

      & .c-tile__tag {
        color: var(--tag-text-hover-color);
        border-color: var(--tag-border-hover-color);
      }
    }
  }

  &--limit-lines {
    & .c-tile__description {
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
              line-clamp: 2;
      -webkit-box-orient: vertical;

      @screen tablet-wide {
        -webkit-line-clamp: 4;
                line-clamp: 4;
        -webkit-box-orient: vertical;
      }

      flex: 1;
    }
  }

}

</style>
